import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { 
  BarChart3, Users, DollarSign, Trophy, ShoppingBag, 
  TrendingUp, Activity, Gift, ArrowUp, ArrowDown 
} from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import '../../styles/admin-gaming.css';

// User Growth Chart Component
const UserGrowthChart = ({ data, locale }) => {
  const chartData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;
    
    const counts = data.map(d => parseInt(d.count || 0));
    const maxCount = Math.max(...counts, 1);
    const minDate = new Date(data[0]?.date);
    const maxDate = new Date(data[data.length - 1]?.date);
    
    return {
      points: data.map((d, index) => ({
        date: d.date,
        count: parseInt(d.count || 0),
        x: index,
        y: maxCount > 0 ? parseInt(d.count || 0) / maxCount : 0
      })),
      maxCount,
      minDate,
      maxDate,
      total: counts.reduce((sum, c) => sum + c, 0)
    };
  }, [data]);

  if (!chartData) return <div className="text-gray-400 text-center py-8">No data available</div>;

  const chartHeight = 200;
  const chartWidth = 600;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;
  const stepX = innerWidth / (chartData.points.length - 1 || 1);

  return (
    <div className="relative overflow-x-auto">
      <svg width={chartWidth} height={chartHeight} className="overflow-visible">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + (1 - ratio) * innerHeight;
          const value = Math.round(chartData.maxCount * ratio);
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                y1={y}
                x2={chartWidth - padding.right}
                y2={y}
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="1"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                fill="rgba(255, 255, 255, 0.5)"
                fontSize="10"
                textAnchor="end"
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Area fill */}
        <path
          d={`M ${padding.left},${padding.top + innerHeight} ${chartData.points.map((p, i) => 
            `L ${padding.left + i * stepX},${padding.top + (1 - p.y) * innerHeight}`
          ).join(' ')} L ${padding.left + (chartData.points.length - 1) * stepX},${padding.top + innerHeight} Z`}
          fill="rgba(59, 130, 246, 0.2)"
        />

        {/* Line */}
        <polyline
          points={chartData.points.map((p, i) => 
            `${padding.left + i * stepX},${padding.top + (1 - p.y) * innerHeight}`
          ).join(' ')}
          fill="none"
          stroke="rgb(59, 130, 246)"
          strokeWidth="2"
        />

        {/* Data points */}
        {chartData.points.map((point, index) => (
          <g key={index}>
            <circle
              cx={padding.left + index * stepX}
              cy={padding.top + (1 - point.y) * innerHeight}
              r="4"
              fill="rgb(59, 130, 246)"
            />
            {index % Math.max(1, Math.ceil(chartData.points.length / 6)) === 0 && (
              <text
                x={padding.left + index * stepX}
                y={chartHeight - 10}
                fill="rgba(255, 255, 255, 0.6)"
                fontSize="10"
                textAnchor="middle"
              >
                {new Date(point.date).toLocaleDateString(locale || 'it-IT', { day: '2-digit', month: '2-digit' })}
              </text>
            )}
          </g>
        ))}
      </svg>
      <div className="mt-4 flex items-center justify-between text-sm text-gray-400">
        <span>Total: {chartData.total} users</span>
        <span>{new Date(chartData.minDate).toLocaleDateString(locale || 'it-IT')} - {new Date(chartData.maxDate).toLocaleDateString(locale || 'it-IT')}</span>
      </div>
    </div>
  );
};

// Card Rarity Distribution Chart Component
const RarityDistributionChart = ({ data }) => {
  const chartData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;
    
    const maxValue = Math.max(...data.map(d => parseInt(d.total_cards || 0)), 1);
    
    return {
      items: data.map(d => ({
        rarity: d.rarity || 'Unknown',
        totalCards: parseInt(d.total_cards || 0),
        ownedCards: parseInt(d.owned_cards || 0),
        companyCards: parseInt(d.company_cards || 0),
        listedCards: parseInt(d.listed_cards || 0)
      })),
      maxValue
    };
  }, [data]);

  if (!chartData) return <div className="text-gray-400 text-center py-8">No data available</div>;

  const chartHeight = 200;
  const chartWidth = 600;
  const padding = { top: 20, right: 20, bottom: 60, left: 50 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;
  const barWidth = Math.max(20, (innerWidth / chartData.items.length) - 10);

  // Colori fissi per segmenti: devono coincidere con la legenda (Owned, Company, Listed)
  const segmentColors = {
    owned: 'rgb(59, 130, 246)',           // blu – come in legenda "Owned"
    company: 'rgba(107, 114, 128, 0.8)',  // grigio – come in legenda "Company"
    listed: 'rgba(234, 179, 8, 0.9)'      // oro – come in legenda "Listed"
  };

  return (
    <div className="relative overflow-x-auto">
      <svg width={chartWidth} height={chartHeight} className="overflow-visible">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + (1 - ratio) * innerHeight;
          const value = Math.round(chartData.maxValue * ratio);
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                y1={y}
                x2={chartWidth - padding.right}
                y2={y}
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="1"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                fill="rgba(255, 255, 255, 0.5)"
                fontSize="10"
                textAnchor="end"
              >
                {value.toLocaleString()}
              </text>
            </g>
          );
        })}

        {/* Stacked bars */}
        {chartData.items.map((item, index) => {
          const x = padding.left + index * (innerWidth / chartData.items.length);
          let currentY = padding.top + innerHeight;
          
          const segments = [
            { value: item.ownedCards, label: 'Owned', color: segmentColors.owned },
            { value: item.companyCards, label: 'Company', color: segmentColors.company },
            { value: item.listedCards, label: 'Listed', color: segmentColors.listed }
          ];

          return (
            <g key={index}>
              {segments.map((segment) => {
                const height = (segment.value / chartData.maxValue) * innerHeight;
                const bar = (
                  <rect
                    key={segment.label}
                    x={x}
                    y={currentY - height}
                    width={barWidth}
                    height={height}
                    fill={segment.color}
                    opacity={segment.value > 0 ? 0.8 : 0}
                  />
                );
                currentY -= height;
                return bar;
              })}
              
              {/* Rarity label */}
              <text
                x={x + barWidth / 2}
                y={chartHeight - 10}
                fill="rgba(255, 255, 255, 0.6)"
                fontSize="10"
                textAnchor="middle"
                transform={`rotate(-45 ${x + barWidth / 2} ${chartHeight - 10})`}
              >
                {item.rarity}
              </text>
            </g>
          );
        })}
      </svg>
      
      {/* Legend - stessi colori usati nei segmenti delle barre */}
      <div className="flex flex-wrap gap-4 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: segmentColors.owned }} />
          <span className="text-sm text-gray-400">Owned</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: segmentColors.company }} />
          <span className="text-sm text-gray-400">Company</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: segmentColors.listed }} />
          <span className="text-sm text-gray-400">Listed</span>
        </div>
      </div>
    </div>
  );
};

// GameWeek Trends Chart Component
const GameWeekTrendsChart = ({ data, locale }) => {
  const chartData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;
    
    const participants = data.map(d => parseInt(d.total_participants || 0));
    const maxParticipants = Math.max(...participants, 1);
    
    return {
      points: data.map((d, index) => ({
        date: d.date,
        gameweekName: d.gameweek_name || `GW ${d.gameweek_id}`,
        participants: parseInt(d.total_participants || 0),
        tournaments: parseInt(d.tournament_count || 0),
        buyIns: parseFloat(d.total_buy_ins || 0),
        prizePool: parseFloat(d.total_prize_pool || 0),
        status: d.status,
        x: index,
        y: maxParticipants > 0 ? parseInt(d.total_participants || 0) / maxParticipants : 0
      })),
      maxParticipants
    };
  }, [data]);

  if (!chartData) return <div className="text-gray-400 text-center py-8">No data available</div>;

  const chartHeight = 200;
  const chartWidth = 600;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;
  const stepX = innerWidth / (chartData.points.length - 1 || 1);

  return (
    <div className="relative overflow-x-auto">
      <svg width={chartWidth} height={chartHeight} className="overflow-visible">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + (1 - ratio) * innerHeight;
          const value = Math.round(chartData.maxParticipants * ratio);
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                y1={y}
                x2={chartWidth - padding.right}
                y2={y}
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="1"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                fill="rgba(255, 255, 255, 0.5)"
                fontSize="10"
                textAnchor="end"
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Area fill */}
        <path
          d={`M ${padding.left},${padding.top + innerHeight} ${chartData.points.map((p, i) => 
            `L ${padding.left + i * stepX},${padding.top + (1 - p.y) * innerHeight}`
          ).join(' ')} L ${padding.left + (chartData.points.length - 1) * stepX},${padding.top + innerHeight} Z`}
          fill="rgba(234, 179, 8, 0.2)"
        />

        {/* Line */}
        <polyline
          points={chartData.points.map((p, i) => 
            `${padding.left + i * stepX},${padding.top + (1 - p.y) * innerHeight}`
          ).join(' ')}
          fill="none"
          stroke="rgb(234, 179, 8)"
          strokeWidth="2"
        />

        {/* Data points */}
        {chartData.points.map((point, index) => (
          <g key={index}>
            <circle
              cx={padding.left + index * stepX}
              cy={padding.top + (1 - point.y) * innerHeight}
              r="4"
              fill="rgb(234, 179, 8)"
            />
            {index % Math.max(1, Math.ceil(chartData.points.length / 6)) === 0 && (
              <text
                x={padding.left + index * stepX}
                y={chartHeight - 10}
                fill="rgba(255, 255, 255, 0.6)"
                fontSize="10"
                textAnchor="middle"
              >
                {new Date(point.date).toLocaleDateString(locale || 'it-IT', { day: '2-digit', month: '2-digit' })}
              </text>
            )}
          </g>
        ))}
      </svg>
      <div className="mt-4 flex items-center justify-between text-sm text-gray-400">
        <span>Max: {chartData.maxParticipants} participants</span>
        <span>{chartData.points.length} GameWeeks</span>
      </div>
    </div>
  );
};

// Economic Flows Chart Component
const EconomicFlowsChart = ({ data, locale }) => {
  const chartData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;

    // Group by date and type (exclude refund from display)
    const grouped = {};
    const types = new Set();
    
    data
      .filter(d => (d.type || '').toLowerCase() !== 'refund')
      .forEach(d => {
      const date = d.date;
      const type = d.type;
      types.add(type);
      
      if (!grouped[date]) {
        grouped[date] = {};
      }
      grouped[date][type] = parseFloat(d.total || 0);
    });

    const dates = Object.keys(grouped).sort();
    const typeArray = Array.from(types);
    
    // Calculate max value for scaling
    let maxValue = 0;
    dates.forEach(date => {
      const dayTotal = Object.values(grouped[date]).reduce((sum, val) => sum + Math.abs(val), 0);
      maxValue = Math.max(maxValue, dayTotal);
    });

    return {
      dates,
      types: typeArray,
      grouped,
      maxValue: maxValue || 1
    };
  }, [data]);

  if (!chartData) return <div className="text-gray-400 text-center py-8">No data available</div>;

  const chartHeight = 200;
  const chartWidth = 600;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;
  const barWidth = Math.max(4, innerWidth / chartData.dates.length - 2);
  
  const colors = {
    deposit: 'rgb(34, 197, 94)',
    withdrawal: 'rgb(239, 68, 68)',
    buyin: 'rgb(234, 179, 8)',
    prize: 'rgb(168, 85, 247)',
    referral: 'rgb(59, 130, 246)',
    marketplace_fee: 'rgb(236, 72, 153)',
    pack: 'rgb(249, 115, 22)'
  };

  return (
    <div className="relative overflow-x-auto">
      <svg width={chartWidth} height={chartHeight} className="overflow-visible">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + (1 - ratio) * innerHeight;
          const value = Math.round(chartData.maxValue * ratio);
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                y1={y}
                x2={chartWidth - padding.right}
                y2={y}
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="1"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                fill="rgba(255, 255, 255, 0.5)"
                fontSize="10"
                textAnchor="end"
              >
                {value.toLocaleString()}
              </text>
            </g>
          );
        })}

        {/* Stacked bars */}
        {chartData.dates.map((date, dateIndex) => {
          const dayData = chartData.grouped[date];
          const x = padding.left + dateIndex * (innerWidth / chartData.dates.length);
          let currentY = padding.top + innerHeight;
          
          return (
            <g key={dateIndex}>
              {chartData.types.map((type) => {
                const value = Math.abs(dayData?.[type] || 0);
                const height = (value / chartData.maxValue) * innerHeight;
                const bar = (
                  <rect
                    key={type}
                    x={x}
                    y={currentY - height}
                    width={barWidth}
                    height={height}
                    fill={colors[type] || 'rgb(107, 114, 128)'}
                    opacity={value > 0 ? 0.8 : 0}
                  />
                );
                currentY -= height;
                return bar;
              })}
              
              {/* Date labels */}
              {dateIndex % Math.max(1, Math.ceil(chartData.dates.length / 6)) === 0 && (
                <text
                  x={x + barWidth / 2}
                  y={chartHeight - 10}
                  fill="rgba(255, 255, 255, 0.6)"
                  fontSize="10"
                  textAnchor="middle"
                >
                  {new Date(date).toLocaleDateString(locale || 'it-IT', { day: '2-digit', month: '2-digit' })}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      
      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-4">
        {chartData.types.map(type => (
          <div key={type} className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded"
              style={{ backgroundColor: colors[type] || 'rgb(107, 114, 128)' }}
            />
            <span className="text-sm text-gray-400 capitalize">{type.replace('_', ' ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { locale } = useLanguage();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [userGrowth, setUserGrowth] = useState(null);
  const [economicFlows, setEconomicFlows] = useState(null);
  const [rarityDistribution, setRarityDistribution] = useState(null);
  const [gameweekTrends, setGameweekTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30'); // days

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [kpisData, growthData, flowsData, rarityData, trendsData] = await Promise.all([
        adminApi.getKPIs(parseInt(timeRange)),
        adminApi.getUserGrowth(parseInt(timeRange)),
        adminApi.getEconomicFlows(parseInt(timeRange)),
        adminApi.getCardRarityDistribution(),
        adminApi.getGameWeekTrends(90)
      ]);

      console.log('Dashboard data received:', { kpisData, growthData, flowsData, rarityData, trendsData });

      if (kpisData?.success && kpisData.kpis) {
        setKpis(kpisData.kpis);
      } else if (kpisData && !kpisData.error) {
        // Handle case where response is direct data
        setKpis(kpisData);
      }
      
      if (growthData?.success && growthData.growth) {
        setUserGrowth(growthData.growth);
      } else if (growthData && !growthData.error) {
        setUserGrowth(growthData);
      }
      
      if (flowsData?.success && flowsData.flows) {
        setEconomicFlows(flowsData.flows);
      } else if (flowsData && !flowsData.error) {
        setEconomicFlows(flowsData);
      }

      if (rarityData?.success && rarityData.distribution) {
        setRarityDistribution(rarityData.distribution);
      } else if (rarityData && !rarityData.error) {
        setRarityDistribution(rarityData);
      }

      if (trendsData?.success && trendsData.trends) {
        console.log('GameWeek trends data:', trendsData.trends);
        setGameweekTrends(trendsData.trends);
      } else if (trendsData && !trendsData.error && Array.isArray(trendsData)) {
        console.log('GameWeek trends data (direct array):', trendsData);
        setGameweekTrends(trendsData);
      } else {
        console.log('No GameWeek trends data:', trendsData);
        setGameweekTrends([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto"></div>
        <p className="mt-4">Loading dashboard...</p>
      </div>
    );
  }

  const kpiCards = [
    {
      title: 'Total Users',
      value: kpis?.totalUsers || 0,
      icon: Users,
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/10',
      change: kpis?.userGrowth || 0
    },
    {
      title: 'Active Users',
      value: kpis?.activeUsers || 0,
      icon: Activity,
      color: 'text-green-400',
      bgColor: 'bg-green-400/10',
      change: kpis?.activeUserGrowth || 0
    },
    {
      title: 'Total Deposits',
      value: `€${(kpis?.totalDeposits || 0).toLocaleString()}`,
      icon: ArrowDown,
      color: 'text-green-400',
      bgColor: 'bg-green-400/10',
      change: kpis?.depositGrowth || 0
    },
    {
      title: 'Total Withdrawals',
      value: `€${(kpis?.totalWithdrawals || 0).toLocaleString()}`,
      icon: ArrowUp,
      color: 'text-red-400',
      bgColor: 'bg-red-400/10',
      change: kpis?.withdrawalGrowth || 0
    },
    {
      title: 'Active Buy-ins',
      value: `€${(kpis?.activeBuyIns || 0).toLocaleString()}`,
      icon: Trophy,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-400/10',
      change: null
    },
    {
      title: 'Prizes Paid (Gameweek)',
      value: `${(kpis?.currentPrizePools ?? kpis?.prizePools ?? 0).toLocaleString()} DIOS`,
      icon: Gift,
      color: 'text-purple-400',
      bgColor: 'bg-purple-400/10',
      change: null
    },
    {
      title: 'Marketplace Volume',
      value: `€${(kpis?.marketplaceVolume || 0).toLocaleString()}`,
      icon: ShoppingBag,
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-400/10',
      change: kpis?.marketplaceGrowth || 0
    },
    {
      title: 'Fees Generated',
      value: `€${(kpis?.feesGenerated || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'text-orange-400',
      bgColor: 'bg-orange-400/10',
      change: kpis?.feeGrowth || 0
    },
    {
      title: 'Active Auctions',
      value: kpis?.activeAuctions || 0,
      icon: ShoppingBag,
      color: 'text-pink-400',
      bgColor: 'bg-pink-400/10',
      change: null
    },
    {
      title: 'Referral Lines',
      value: kpis?.referralLines || 0,
      icon: Users,
      color: 'text-indigo-400',
      bgColor: 'bg-indigo-400/10',
      change: null
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-4 py-2"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 p-4 mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {kpiCards.map((kpi, index) => {
          const Icon = kpi.icon;
          return (
            <div key={index} className={`${kpi.bgColor} p-6`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${kpi.color}`} />
                {kpi.change !== null && (
                  <span className={`text-xs flex items-center ${
                    kpi.change >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    <TrendingUp className={`w-3 h-3 mr-1 ${kpi.change < 0 ? 'rotate-180' : ''}`} />
                    {Math.abs(kpi.change)}%
                  </span>
                )}
              </div>
              <p className="text-sm mb-1">{kpi.title}</p>
              <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* User Growth */}
        {userGrowth && Array.isArray(userGrowth) && userGrowth.length > 0 && (
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-400" />
              User Growth
            </h2>
            <UserGrowthChart data={userGrowth} locale={locale} />
          </div>
        )}

        {/* Economic Flows */}
        {economicFlows && Array.isArray(economicFlows) && economicFlows.length > 0 && (
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-green-400" />
              Economic Flows
            </h2>
            <EconomicFlowsChart data={economicFlows} locale={locale} />
          </div>
        )}

        {/* Card Rarity Distribution */}
        {rarityDistribution && Array.isArray(rarityDistribution) && rarityDistribution.length > 0 && (
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-purple-400" />
              Card Rarity Distribution
            </h2>
            <RarityDistributionChart data={rarityDistribution} />
          </div>
        )}

        {/* GameWeek Trends */}
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
            GameWeek Trends
          </h2>
          {gameweekTrends && Array.isArray(gameweekTrends) && gameweekTrends.length > 0 ? (
            <GameWeekTrendsChart data={gameweekTrends} locale={locale} />
          ) : (
            <div className="text-gray-400 text-center py-8">
              {loading ? 'Loading GameWeek trends...' : 'No GameWeek data available for the selected period'}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
        <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button 
            onClick={() => navigate('/admin/gameweeks')}
            className="bg-blue-600 hover:bg-blue-700 p-4 text-left transition-colors rounded-lg group"
          >
            <Trophy className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
            <div className="font-semibold text-white">GameWeeks</div>
            <div className="text-sm text-gray-300">Manage competitions</div>
          </button>
          <button 
            onClick={() => navigate('/admin/marketplace')}
            className="bg-green-600 hover:bg-green-700 p-4 text-left transition-colors rounded-lg group"
          >
            <ShoppingBag className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
            <div className="font-semibold text-white">Marketplace</div>
            <div className="text-sm text-gray-300">Manage listings</div>
          </button>
          <button 
            onClick={() => navigate('/admin/logs')}
            className="bg-purple-600 hover:bg-purple-700 p-4 text-left transition-colors rounded-lg group"
          >
            <Activity className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
            <div className="font-semibold text-white">View Logs</div>
            <div className="text-sm text-gray-300">Audit trail</div>
          </button>
          <button 
            onClick={() => navigate('/admin/users')}
            className="bg-orange-600 hover:bg-orange-700 p-4 text-left transition-colors rounded-lg group"
          >
            <Users className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
            <div className="font-semibold text-white">Users</div>
            <div className="text-sm text-gray-300">Manage users</div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
